'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { studioClient } from '@/sanity/lib/client';
import { PERMISSIONS_QUERY } from '@/sanity/queries/permissions';
import { IPermissions, PermissionsContextType } from '@/types/permissions';

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const [permissions, setPermissions] = useState<IPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const data = await studioClient.fetch<IPermissions>(PERMISSIONS_QUERY);
        setPermissions(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching permissions:', error);
        // Set default permissions if fetch fails
        setPermissions({
          _id: 'default',
          isActive: true,
          home: { show: true, showInHeader: true, showInFooter: true },
          companies: { show: true, showInHeader: true, showInFooter: true },
          aboutUs: { show: true, showInHeader: true, showInFooter: true },
          ourValues: { show: true, showInHeader: true, showInFooter: true },
          objective: { show: true, showInHeader: true, showInFooter: true },
          motion: { show: true, showInHeader: true, showInFooter: true },
          ourClients: { show: true, showInHeader: true, showInFooter: true },
          news: { show: true, showInHeader: true, showInFooter: true },
          contactUs: { show: true, showInHeader: true, showInFooter: true },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  const isSectionVisible = (section: keyof Omit<IPermissions, '_id' | 'isActive'>): boolean => {
    if (!permissions || !permissions.isActive) return true; // Default to visible if no permissions
    return permissions[section]?.show ?? true;
  };

  const isSectionVisibleInHeader = (
    section: keyof Omit<IPermissions, '_id' | 'isActive'>
  ): boolean => {
    if (!permissions || !permissions.isActive) return true; // Default to visible if no permissions
    return permissions[section]?.showInHeader ?? true;
  };

  const isSectionVisibleInFooter = (
    section: keyof Omit<IPermissions, '_id' | 'isActive'>
  ): boolean => {
    if (!permissions || !permissions.isActive) return true; // Default to visible if no permissions
    return permissions[section]?.showInFooter ?? true;
  };

  const value: PermissionsContextType = {
    permissions,
    loading,
    isSectionVisible,
    isSectionVisibleInHeader,
    isSectionVisibleInFooter,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    // Return default permissions if context is not available
    return {
      permissions: null,
      loading: true,
      isSectionVisible: () => true,
      isSectionVisibleInHeader: () => true,
      isSectionVisibleInFooter: () => true,
    };
  }
  return context;
}
